// Theme definitions for Brings To Mind
// ═══════════════════════════════════════════════════════════════
// IMPORTS: Core image constants for centralized image management
// ═══════════════════════════════════════════════════════════════
//
// BACKGROUND IMAGE SYSTEM:
// ───────────────────────────────────────────────────────────────
// Each theme can optionally specify a custom backgroundImage property.
//
// DEFAULT BEHAVIOR:
// - If no backgroundImage is specified, uses /images/waves_gray_abstract.png
// - The grayscale image is blended with var(--color-primary) using soft-light mode
// - This creates a theme-colored background automatically
// - Opacity is set to 0.15 for subtle effect
//
// CUSTOM IMAGES:
// - To use a theme-specific image, add: backgroundImage: '/images/your-image.jpg'
// - Custom images will still be blended with the theme's primary color
// - Works best with desaturated/grayscale images for consistent color blending
// - Images are stretched to cover the entire viewport (background-size: cover)
//
// EXAMPLE:
//   sunset: {
//     backgroundImage: '/images/sunset-waves.jpg', // Optional custom image
//     colors: { ... }
//   }
// ═══════════════════════════════════════════════════════════════
import { IMAGES } from '../constants/images';

export const themes = {
  default: {
    name: 'Default',
    description: 'Classic blue and gray theme',
    // backgroundImage: '/images/custom-bg.jpg', // Optional: specify custom background image for this theme
    colors: {
      // Primary colors
      primary: '#3b82f6',
      primaryHover: '#2563eb',
      primaryLight: '#93c5fd',

      // Secondary colors
      secondary: '#667eea',
      secondaryHover: '#5568d3',

      // Background colors
      background: '#f3f4f6',
      backgroundAlt: '#e5e7eb',
      backgroundDark: '#1f2937',

      // Text colors
      text: '#1f2937',
      textLight: '#6b7280',
      textMuted: '#374151',
      textInverse: '#ffffff',

      // UI elements
      border: '#d1d5db',
      borderLight: '#e5e7eb',
      success: '#10b981',
      successHover: '#059669',
      error: '#ef4444',
      errorHover: '#dc2626',
      warning: '#f59e0b',
      info: '#3b82f6',

      // Header - yellow gradient
      headerBgStart: '#F5FBC2',
      headerBgEnd: '#F0E266',
      headerText: '#1f2937',
      headerTextSecondary: '#6b7280',

      // Text on background image (for Settings/Admin page headers)
      textOnBackground: '#ffffff', // White text for dark blended background

      // App background gradient
      gradientStart: '#667eea',
      gradientEnd: '#764ba2',

      // Yellow gradient for active states
      yellowGradientStart: '#F5FBC2',
      yellowGradientEnd: '#F0E266',

      // Active states
      activeBg: '#fde047',
      activeText: '#1f2937',

      // Input colors
      inputBg: '#ffffff',
      inputFocus: '#3b82f6',

      // Card colors
      cardBg: '#ffffff',

      // Button text colors
      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states
      reflectionShowBg: '#dbeafe',
      reflectionEditBg: '#e0e7ff',
      reflectionShowShadow: 'rgba(59,130,246,0.15)',
      reflectionEditShadow: 'rgba(102,126,234,0.15)',

      // Subtle diagonal stripe pattern
      '--background-pattern': 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 70px)',
      '--shadow-sm': '0 1px 3px rgba(0,0,0,0.1)',
      '--shadow-md': '0 2px 4px rgba(0,0,0,0.1)',
      '--shadow-lg': '0 10px 15px rgba(0,0,0,0.1)',
      '--radius-card': '8px',
      '--radius-button': '8px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🧠'
  },

  ocean: {
    name: 'Ocean',
    description: 'Calming blue ocean theme',
    colors: {
      primary: '#0ea5e9',
      primaryHover: '#0284c7',
      primaryLight: '#7dd3fc',

      secondary: '#0c4a6e',
      secondaryHover: '#082f49',

      background: '#f0f9ff',
      backgroundAlt: '#e0f2fe',
      backgroundDark: '#0c4a6e',

      text: '#0f172a',
      textLight: '#475569',
      textMuted: '#334155',
      textInverse: '#f0f9ff',

      border: '#7dd3fc',
      borderLight: '#bae6fd',
      success: '#06b6d4',
      successHover: '#0891b2',
      error: '#dc2626',
      errorHover: '#b91c1c',
      warning: '#f97316',
      info: '#0ea5e9',

      headerBgStart: '#0c4a6e',
      headerBgEnd: '#075985',
      headerText: '#f0f9ff',
      headerTextSecondary: '#bae6fd',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#0ea5e9',
      gradientEnd: '#0c4a6e',

      yellowGradientStart: '#7dd3fc',
      yellowGradientEnd: '#38bdf8',

      activeBg: '#7dd3fc',
      activeText: '#0c4a6e',

      inputBg: '#ffffff',
      inputFocus: '#0ea5e9',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - ocean blues
      reflectionShowBg: '#bae6fd',
      reflectionEditBg: '#e0f2fe',
      reflectionShowShadow: 'rgba(14,165,233,0.2)',
      reflectionEditShadow: 'rgba(125,211,252,0.2)',

      // Wave-like pattern
      '--background-pattern': 'repeating-linear-gradient(0deg, rgba(14,165,233,0.03) 0px, transparent 2px, transparent 40px, rgba(14,165,233,0.03) 42px)',
      '--shadow-sm': '0 1px 3px rgba(14,165,233,0.1)',
      '--shadow-md': '0 4px 6px rgba(14,165,233,0.12)',
      '--shadow-lg': '0 10px 20px rgba(14,165,233,0.15)',
      '--radius-card': '12px',
      '--radius-button': '12px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🌊'
  },

  sunset: {
    name: 'Sunset',
    description: 'Warm orange and purple sunset theme',
    colors: {
      primary: '#f97316',
      primaryHover: '#ea580c',
      primaryLight: '#fdba74',

      secondary: '#7c2d12',
      secondaryHover: '#581c0c',

      background: '#fff7ed',
      backgroundAlt: '#ffedd5',
      backgroundDark: '#7c2d12',

      text: '#1c1917',
      textLight: '#78716c',
      textMuted: '#57534e',
      textInverse: '#fff7ed',

      border: '#fdba74',
      borderLight: '#fed7aa',
      success: '#84cc16',
      successHover: '#65a30d',
      error: '#dc2626',
      errorHover: '#b91c1c',
      warning: '#eab308',
      info: '#f97316',

      headerBgStart: '#7c2d12',
      headerBgEnd: '#9a3412',
      headerText: '#fff7ed',
      headerTextSecondary: '#fed7aa',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#f97316',
      gradientEnd: '#7c2d12',

      yellowGradientStart: '#fdba74',
      yellowGradientEnd: '#fb923c',

      activeBg: '#fdba74',
      activeText: '#7c2d12',

      inputBg: '#ffffff',
      inputFocus: '#f97316',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - sunset colors
      reflectionShowBg: '#fed7aa',
      reflectionEditBg: '#ffedd5',
      reflectionShowShadow: 'rgba(249,115,22,0.2)',
      reflectionEditShadow: 'rgba(253,186,116,0.2)',

      // Sunset radial gradient pattern
      '--background-pattern': 'radial-gradient(circle at 80% 20%, rgba(249,115,22,0.08) 0%, transparent 50%)',
      '--shadow-sm': '0 2px 4px rgba(249,115,22,0.1)',
      '--shadow-md': '0 4px 8px rgba(249,115,22,0.12)',
      '--shadow-lg': '0 10px 20px rgba(249,115,22,0.15)',
      '--radius-card': '14px',
      '--radius-button': '14px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🌅'
  },

  forest: {
    name: 'Forest',
    description: 'Natural green forest theme',
    colors: {
      primary: '#22c55e',
      primaryHover: '#16a34a',
      primaryLight: '#86efac',

      secondary: '#14532d',
      secondaryHover: '#052e16',

      background: '#f0fdf4',
      backgroundAlt: '#dcfce7',
      backgroundDark: '#14532d',

      text: '#1c1917',
      textLight: '#57534e',
      textMuted: '#44403c',
      textInverse: '#f0fdf4',

      border: '#86efac',
      borderLight: '#bbf7d0',
      success: '#22c55e',
      successHover: '#16a34a',
      error: '#dc2626',
      errorHover: '#b91c1c',
      warning: '#eab308',
      info: '#06b6d4',

      headerBgStart: '#14532d',
      headerBgEnd: '#166534',
      headerText: '#f0fdf4',
      headerTextSecondary: '#bbf7d0',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#22c55e',
      gradientEnd: '#14532d',

      yellowGradientStart: '#86efac',
      yellowGradientEnd: '#4ade80',

      activeBg: '#86efac',
      activeText: '#14532d',

      inputBg: '#ffffff',
      inputFocus: '#22c55e',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - forest greens
      reflectionShowBg: '#bbf7d0',
      reflectionEditBg: '#dcfce7',
      reflectionShowShadow: 'rgba(34,197,94,0.2)',
      reflectionEditShadow: 'rgba(134,239,172,0.2)',

      // Organic forest pattern - leaves/texture
      '--background-pattern': 'radial-gradient(circle at 25% 25%, rgba(34,197,94,0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(34,197,94,0.05) 0%, transparent 50%)',
      '--shadow-sm': '0 1px 3px rgba(34,197,94,0.1)',
      '--shadow-md': '0 4px 6px rgba(34,197,94,0.12)',
      '--shadow-lg': '0 10px 20px rgba(34,197,94,0.15)',
      '--radius-card': '16px',
      '--radius-button': '16px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🌲'
  },

  midnight: {
    name: 'Midnight',
    description: 'Dark purple midnight theme',
    colors: {
      primary: '#a855f7',
      primaryHover: '#9333ea',
      primaryLight: '#d8b4fe',

      secondary: '#581c87',
      secondaryHover: '#3b0764',

      background: '#faf5ff',
      backgroundAlt: '#f3e8ff',
      backgroundDark: '#581c87',

      text: '#1f2937',
      textLight: '#6b7280',
      textMuted: '#374151',
      textInverse: '#faf5ff',

      border: '#d8b4fe',
      borderLight: '#e9d5ff',
      success: '#10b981',
      successHover: '#059669',
      error: '#f43f5e',
      errorHover: '#e11d48',
      warning: '#f59e0b',
      info: '#a855f7',

      headerBgStart: '#581c87',
      headerBgEnd: '#6b21a8',
      headerText: '#faf5ff',
      headerTextSecondary: '#e9d5ff',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#a855f7',
      gradientEnd: '#581c87',

      yellowGradientStart: '#d8b4fe',
      yellowGradientEnd: '#c084fc',

      activeBg: '#d8b4fe',
      activeText: '#581c87',

      inputBg: '#ffffff',
      inputFocus: '#a855f7',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - midnight purples
      reflectionShowBg: '#e9d5ff',
      reflectionEditBg: '#f3e8ff',
      reflectionShowShadow: 'rgba(168,85,247,0.2)',
      reflectionEditShadow: 'rgba(216,180,254,0.2)',

      // Starry night pattern
      '--background-pattern': 'radial-gradient(circle, rgba(168,85,247,0.1) 1px, transparent 1px), radial-gradient(circle, rgba(168,85,247,0.06) 1px, transparent 1px)',
      '--background-size': '50px 50px, 80px 80px',
      '--shadow-sm': '0 2px 4px rgba(168,85,247,0.1)',
      '--shadow-md': '0 4px 8px rgba(168,85,247,0.12)',
      '--shadow-lg': '0 10px 20px rgba(168,85,247,0.15)',
      '--radius-card': '12px',
      '--radius-button': '12px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🌙'
  },

  mindful: {
    name: 'Mindful Minimalism',
    description: 'Clean, modern, professional with subtle depth',
    colors: {
      primary: '#6366f1',
      primaryHover: '#4f46e5',
      primaryLight: '#a5b4fc',

      secondary: '#8b5cf6',
      secondaryHover: '#7c3aed',

      background: '#fafaf9',
      backgroundAlt: '#f5f5f4',
      backgroundDark: '#0f172a',

      text: '#0f172a',
      textLight: '#64748b',
      textMuted: '#475569',
      textInverse: '#ffffff',

      border: '#e2e8f0',
      borderLight: '#f1f5f9',
      success: '#10b981',
      successHover: '#059669',
      error: '#ef4444',
      errorHover: '#dc2626',
      warning: '#f59e0b',
      info: '#6366f1',

      headerBgStart: '#ffffff',
      headerBgEnd: '#ffffff',
      headerText: '#0f172a',
      headerTextSecondary: '#64748b',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#fafaf9',
      gradientEnd: '#f5f5f4',

      yellowGradientStart: '#c7d2fe',
      yellowGradientEnd: '#a5b4fc',

      activeBg: '#a5b4fc',
      activeText: '#1e293b',

      inputBg: '#ffffff',
      inputFocus: '#6366f1',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states
      reflectionShowBg: '#dbeafe',
      reflectionEditBg: '#e0e7ff',
      reflectionShowShadow: 'rgba(99,102,241,0.15)',
      reflectionEditShadow: 'rgba(139,92,246,0.15)',

      // Enhanced features for Mindful theme
      '--shadow-sm': '0 1px 3px rgba(0,0,0,0.05)',
      '--shadow-md': '0 4px 6px rgba(0,0,0,0.05)',
      '--shadow-lg': '0 20px 25px rgba(0,0,0,0.04)',
      '--radius-card': '12px',
      '--radius-button': '8px',

      // Mindful background - subtle dot pattern
      '--background-pattern': 'radial-gradient(circle, rgba(99,102,241,0.08) 1px, transparent 1px)',
      '--background-size': '20px 20px'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🧘'
  },

  zen: {
    name: 'Zen Garden',
    description: 'Organic, earthy, grounded in natural calm',
    colors: {
      primary: '#059669',
      primaryHover: '#047857',
      primaryLight: '#6ee7b7',

      secondary: '#0891b2',
      secondaryHover: '#0e7490',

      background: '#f0fdf4',
      backgroundAlt: '#ecfeff',
      backgroundDark: '#134e4a',

      text: '#1c1917',
      textLight: '#57534e',
      textMuted: '#78716c',
      textInverse: '#f0fdf4',

      border: '#d1fae5',
      borderLight: '#d9f99d',
      success: '#059669',
      successHover: '#047857',
      error: '#dc2626',
      errorHover: '#b91c1c',
      warning: '#f97316',
      info: '#0891b2',

      headerBgStart: '#dcfce7',
      headerBgEnd: '#e0f2fe',
      headerText: '#134e4a',
      headerTextSecondary: '#115e59',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#f0fdf4',
      gradientEnd: '#ecfeff',

      yellowGradientStart: '#a7f3d0',
      yellowGradientEnd: '#6ee7b7',

      activeBg: '#a7f3d0',
      activeText: '#134e4a',

      inputBg: '#ffffff',
      inputFocus: '#059669',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - nature inspired
      reflectionShowBg: '#d1fae5',
      reflectionEditBg: '#cffafe',
      reflectionShowShadow: 'rgba(5,150,105,0.2)',
      reflectionEditShadow: 'rgba(8,145,178,0.2)',

      // Nature-inspired shadows with green tint
      '--shadow-sm': '0 1px 3px rgba(5,150,105,0.08)',
      '--shadow-md': '0 4px 6px rgba(5,150,105,0.1)',
      '--shadow-lg': '0 20px 25px rgba(5,150,105,0.08)',
      '--radius-card': '16px',
      '--radius-button': '16px',

      // Zen background pattern - subtle bamboo-like grid
      '--background-pattern': 'repeating-linear-gradient(90deg, rgba(5,150,105,0.03) 0px, transparent 1px, transparent 40px, rgba(5,150,105,0.03) 41px), repeating-linear-gradient(0deg, rgba(5,150,105,0.03) 0px, transparent 1px, transparent 40px, rgba(5,150,105,0.03) 41px)'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🌿'
  },

  focus: {
    name: 'Focus Flow',
    description: 'Clean, high-contrast, distraction-free workspace',
    colors: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      primaryLight: '#93c5fd',

      secondary: '#7c3aed',
      secondaryHover: '#6d28d9',

      background: '#ffffff',
      backgroundAlt: '#f9fafb',
      backgroundDark: '#111827',

      text: '#111827',
      textLight: '#6b7280',
      textMuted: '#9ca3af',
      textInverse: '#ffffff',

      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      success: '#16a34a',
      successHover: '#15803d',
      error: '#dc2626',
      errorHover: '#b91c1c',
      warning: '#eab308',
      info: '#2563eb',

      headerBgStart: '#ffffff',
      headerBgEnd: '#f9fafb',
      headerText: '#111827',
      headerTextSecondary: '#6b7280',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#ffffff',
      gradientEnd: '#f9fafb',

      yellowGradientStart: '#fef3c7',
      yellowGradientEnd: '#fde047',

      activeBg: '#fde047',
      activeText: '#111827',

      inputBg: '#ffffff',
      inputFocus: '#2563eb',

      cardBg: '#ffffff',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - high contrast
      reflectionShowBg: '#dbeafe',
      reflectionEditBg: '#f3e8ff',
      reflectionShowShadow: 'rgba(37,99,235,0.15)',
      reflectionEditShadow: 'rgba(124,58,237,0.15)',

      // Minimal shadows, emphasis on borders
      '--shadow-sm': '0 0 0 1px rgba(0,0,0,0.05)',
      '--shadow-md': '0 1px 3px rgba(0,0,0,0.1)',
      '--shadow-lg': '0 4px 6px rgba(0,0,0,0.08)',
      '--radius-card': '4px',
      '--radius-button': '4px',

      // Clean, no background pattern - solid white
      '--background-pattern': 'none'
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    icon: '🎯'
  },

  warmWelcome: {
    name: 'Warm Welcome',
    description: 'Inviting, personable, human-centered',
    colors: {
      primary: '#f97316',
      primaryHover: '#ea580c',
      primaryLight: '#fdba74',

      secondary: '#ec4899',
      secondaryHover: '#db2777',

      background: '#fff7ed',
      backgroundAlt: '#fed7aa',
      backgroundDark: '#78350f',

      text: '#78350f',
      textLight: '#92400e',
      textMuted: '#a16207',
      textInverse: '#fffbeb',

      border: '#fbbf24',
      borderLight: '#fcd34d',
      success: '#34d399',
      successHover: '#10b981',
      error: '#f87171',
      errorHover: '#ef4444',
      warning: '#fbbf24',
      info: '#60a5fa',

      headerBgStart: '#fdba74',
      headerBgEnd: '#fb923c',
      headerText: '#78350f',
      headerTextSecondary: '#92400e',

      // Text on background image
      textOnBackground: '#ffffff', // White text for dark blended background

      gradientStart: '#fff7ed',
      gradientEnd: '#fed7aa',

      yellowGradientStart: '#fef3c7',
      yellowGradientEnd: '#fde68a',

      activeBg: '#fde68a',
      activeText: '#78350f',

      inputBg: '#fffbeb',
      inputFocus: '#f97316',

      cardBg: '#fffbeb',

      buttonText: '#ffffff', // Legacy support
      darkButtonText: '#ffffff',
      lightButtonText: '#000000',

      // Reflection selection states - warm colors
      reflectionShowBg: '#fed7aa',
      reflectionEditBg: '#fce7f3',
      reflectionShowShadow: 'rgba(249,115,22,0.25)',
      reflectionEditShadow: 'rgba(236,72,153,0.25)',

      // Warm shadows with orange/amber tint
      '--shadow-sm': '0 2px 4px rgba(249,115,22,0.1)',
      '--shadow-md': '0 4px 8px rgba(249,115,22,0.12)',
      '--shadow-lg': '0 20px 25px rgba(249,115,22,0.15)',
      '--radius-card': '20px',
      '--radius-button': '16px',

      // Playful animation timing
      '--animation-timing': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      '--animation-duration': '0.3s',

      // Warm Welcome background - radial gradients like sunset
      '--background-pattern': 'radial-gradient(circle at 20% 50%, rgba(249,115,22,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(236,72,153,0.12) 0%, transparent 50%)'
    },
    typography: {
      fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      headingWeight: 700,
      bodyWeight: 400
    },
    images: {
      // Using core image constants - can override per theme if needed
      logo: IMAGES.LOGO,
      logoMono: IMAGES.LOGO_MONO,
      banner: IMAGES.BANNER,
      backgroundWaves: IMAGES.BACKGROUND_WAVES,
    },
    assets: {
      emptyStateIllustration: '',
      loginBackground: '',
      successIcon: ''
    },
    icon: '☀️'
  }
};

export const getTheme = (themeName) => {
  return themes[themeName] || themes.default;
};

export const getThemesList = () => {
  return Object.keys(themes).map(key => ({
    id: key,
    ...themes[key]
  }));
};
